import { useContext, useState } from 'react';
import { Button } from 'uxd-design-system-components';
import PropTypes from 'prop-types';
import DownloadButtonProps from '~types/components/DownloadButtonProps';
import SendToDelegatorData from '~types/api/SendToDelegatorData';
import getConType from '../../utils/getConType/getConType';
import getSource from '../../utils/getSource/getSource';
import getText from '../../assets/locale/locale';
import useQuery from '../../utils/useQuery/useQuery';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';
import LocaleContext from '../../contexts/LocaleContext';
import { handleAVIError, sendToDelegator } from '../../api/index';

const DownloadButton = ({
  setNotificationContent,
  setNotificationOpen,
  resultsJSON,
  coldContactData,
  userComments,
  displaySendToFAButton,
  planningTopics,
  investmentInvolvementCode,
  label,
}: DownloadButtonProps) => {
  const locId = useContext(LocaleContext);
  const urlQuery = useQuery();
  const conId = urlQuery.get('conId');
  const utmSource = urlQuery.get('utm_source');
  const utmContent = urlQuery.get('utm_content');
  const entityID = urlQuery.get('EID');
  const faNo = urlQuery.get('faNo');

  const [isDownloadBtnLoading, setDownloadBtnLoading] = useState(false);

  function downloadResultsForUser() {
    setDownloadBtnLoading(true);
    addDataLayerTrigger('download-results', {});
    let byteString;
    const fileName = 'MyPriorities-Results.pdf';

    const params = {
      locale: locId,
      sysId: 'PRP',
      appName: 'MyPriorities',
      conType: getConType(utmSource, utmContent),
      utmSource: getSource(utmSource, entityID, faNo, conId),
    };

    const data: SendToDelegatorData = {
      faNo: null,
      branchNo: null,
      userComments,
      sessionId: null,
      coldContactData: null,
      contactNo: conId,
      utmSource: null,
      results: resultsJSON,
      investmentInvolvementCode,
      planningTopics,
      contactName:
        coldContactData && coldContactData.firstName && coldContactData.lastName
          ? `${coldContactData.firstName} ${coldContactData.lastName}`
          : null,
    };

    const responseType = 'blob';

    sendToDelegator(params, data, responseType)
      .then((res) => {
        byteString = res;

        // IE & Edge Legacy
        if (window.navigator && Object.hasOwn(window.navigator, 'msSaveBlob')) {
          (window.navigator as any).msSaveBlob(byteString, fileName);
        } else {
          const blob = new Blob([byteString], { type: 'application/pdf' });
          const fileUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileUrl;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      })
      .catch((err) => {
        if (err.response && err.response.status === 403) {
          handleAVIError(err, locId);
        }
        setNotificationContent({
          message: getText(locId, 'pdfErrorMessage', 'resultsPage'),
          type: 'error',
        });
        setNotificationOpen(true);
        /* eslint-disable no-console */
        console.error(err);
      })
      .finally(() => {
        setDownloadBtnLoading(false);
      });
  }

  return (
    <Button
      loading={isDownloadBtnLoading}
      icon="data-download"
      label={label}
      styleVariant={displaySendToFAButton ? 'secondary' : 'primary'}
      onClick={() => downloadResultsForUser()}
      attributes={{
        value: '',
        id: 'download-button',
        'data-testid': 'download-button',
      }}
    />
  );
};

DownloadButton.propTypes = {
  setNotificationContent: PropTypes.func.isRequired,
  setNotificationOpen: PropTypes.func.isRequired,
  coldContactData: PropTypes.objectOf(PropTypes.string),
  userComments: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  resultsJSON: PropTypes.objectOf(PropTypes.object).isRequired,
  displaySendToFAButton: PropTypes.bool.isRequired,
  planningTopics: PropTypes.arrayOf(PropTypes.string),
  investmentInvolvementCode: PropTypes.string,
  label: PropTypes.string.isRequired,
};

DownloadButton.defaultProps = {
  coldContactData: null,
  userComments: null,
};

export default DownloadButton;
